import React from 'react';
import { Button, Card, CardContent, Flex, Text, LoadingOrbit, TruncatedText, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { Warning, ArrowRight } from '@phosphor-icons/react';
import { useIssueDetailV2 } from '../../hooks/useIssuesV2';

type LinkedIssueCardProps = {
  issueId: string;
  onOpenDrawer: () => void;
};

const severityVariants: Record<string, 'destructive' | 'warning' | 'secondary'> = {
  critical: 'destructive',
  high: 'destructive',
  medium: 'warning',
  low: 'secondary',
  info: 'secondary',
};

export const LinkedIssueCard: React.FC<LinkedIssueCardProps> = ({ issueId, onOpenDrawer }) => {
  const { data, isLoading, isError } = useIssueDetailV2(issueId, { page: 1, pageSize: 1 });

  if (isLoading) {
    return (
      <Card padding="md" className="bg-destructive/5 border-destructive/20">
        <CardContent>
          <Flex align="center" justify="center" gap="sm">
            <LoadingOrbit size="sm" />
            <Text size="sm" className="text-muted-foreground">
              Loading linked issue...
            </Text>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data?.issue) {
    return (
      <Card padding="md" className="bg-destructive/5 border-destructive/20">
        <CardContent>
          <Flex align="center" gap="sm">
            <Warning size={20} className="text-destructive shrink-0" />
            <Text size="sm" className="text-muted-foreground">
              Failed to load linked issue
            </Text>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  const issue = data.issue;
  const severityVariant = severityVariants[issue.severity] ?? 'secondary';

  return (
    <Card padding="md" className="bg-destructive/5 border-destructive/20">
      <CardContent>
        <Flex direction="column" gap="sm">
          <Flex align="center" gap="sm">
            <Warning size={20} className="text-destructive shrink-0" weight="fill" />
            <Text size="sm" weight="medium" className="text-destructive">
              Linked Issue
            </Text>
          </Flex>
          <Flex align="start" justify="between" gap="md">
            <div className="flex-1 min-w-0">
              <TruncatedText text={issue.title} lines={2} className="text-sm font-medium text-foreground" />
              <Flex align="center" gap="xs" className="mt-1">
                <TagBadge name={issue.severity} color={badgeVariantToTagColor(severityVariant)} hideDot size="sm" />
                {issue.category?.label && (
                  <Text size="xs" className="text-muted-foreground">
                    {issue.category.label}
                  </Text>
                )}
              </Flex>
            </div>
            <Button
              variant="outline"
              size="sm"
              onPress={onOpenDrawer}
              iconRight={<ArrowRight size={14} />}
            >
              View Issue
            </Button>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
};
